package org.teddapp.project.crypto

/**
 * PBKDF2-HMAC-SHA256 en Kotlin pur, pour les plateformes qui n'offrent pas de
 * dérivation synchrone (le navigateur). Donne les mêmes octets que la mise en œuvre
 * du système sur les autres plateformes : un test le vérifie.
 *
 * La boucle travaille sur des mots de 32 bits, sans repasser par des octets : deux
 * compressions par itération, les états des deux masques de HMAC étant calculés une fois.
 */
internal object Pbkdf2 {
    private val K = intArrayOf(
        0x428a2f98, 0x71374491, 0xb5c0fbcf.toInt(), 0xe9b5dba5.toInt(),
        0x3956c25b, 0x59f111f1, 0x923f82a4.toInt(), 0xab1c5ed5.toInt(),
        0xd807aa98.toInt(), 0x12835b01, 0x243185be, 0x550c7dc3,
        0x72be5d74, 0x80deb1fe.toInt(), 0x9bdc06a7.toInt(), 0xc19bf174.toInt(),
        0xe49b69c1.toInt(), 0xefbe4786.toInt(), 0x0fc19dc6, 0x240ca1cc,
        0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152.toInt(), 0xa831c66d.toInt(), 0xb00327c8.toInt(), 0xbf597fc7.toInt(),
        0xc6e00bf3.toInt(), 0xd5a79147.toInt(), 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
        0x650a7354, 0x766a0abb, 0x81c2c92e.toInt(), 0x92722c85.toInt(),
        0xa2bfe8a1.toInt(), 0xa81a664b.toInt(), 0xc24b8b70.toInt(), 0xc76c51a3.toInt(),
        0xd192e819.toInt(), 0xd6990624.toInt(), 0xf40e3585.toInt(), 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
        0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814.toInt(), 0x8cc70208.toInt(),
        0x90befffa.toInt(), 0xa4506ceb.toInt(), 0xbef9a3f7.toInt(), 0xc67178f2.toInt(),
    )
    private val IV = intArrayOf(
        0x6a09e667, 0xbb67ae85.toInt(), 0x3c6ef372, 0xa54ff53a.toInt(),
        0x510e527f, 0x9b05688c.toInt(), 0x1f83d9ab, 0x5be0cd19,
    )

    private fun rotr(x: Int, n: Int) = (x ushr n) or (x shl (32 - n))

    /** Compresse le bloc dont les seize premiers mots sont dans [w] ; [w] sert de tampon. */
    private fun compress(state: IntArray, w: IntArray) {
        for (i in 16 until 64) {
            val s0 = rotr(w[i - 15], 7) xor rotr(w[i - 15], 18) xor (w[i - 15] ushr 3)
            val s1 = rotr(w[i - 2], 17) xor rotr(w[i - 2], 19) xor (w[i - 2] ushr 10)
            w[i] = w[i - 16] + s0 + w[i - 7] + s1
        }
        var a = state[0]; var b = state[1]; var c = state[2]; var d = state[3]
        var e = state[4]; var f = state[5]; var g = state[6]; var h = state[7]
        for (i in 0 until 64) {
            val t1 = h + (rotr(e, 6) xor rotr(e, 11) xor rotr(e, 25)) + ((e and f) xor (e.inv() and g)) + K[i] + w[i]
            val t2 = (rotr(a, 2) xor rotr(a, 13) xor rotr(a, 22)) + ((a and b) xor (a and c) xor (b and c))
            h = g; g = f; f = e; e = d + t1; d = c; c = b; b = a; a = t1 + t2
        }
        state[0] += a; state[1] += b; state[2] += c; state[3] += d
        state[4] += e; state[5] += f; state[6] += g; state[7] += h
    }

    /** Absorbe [message] à la suite de [prefixBytes] octets déjà compressés dans [state], et termine. */
    private fun finish(state: IntArray, prefixBytes: Int, message: ByteArray, w: IntArray) {
        val total = prefixBytes + message.size
        val padded = ByteArray(((message.size + 9 + 63) / 64) * 64)
        message.copyInto(padded)
        padded[message.size] = 0x80.toByte()
        val bits = total.toLong() * 8
        for (i in 0 until 8) padded[padded.size - 1 - i] = (bits ushr (8 * i)).toByte()
        for (block in 0 until padded.size / 64) {
            for (i in 0 until 16) {
                val o = block * 64 + i * 4
                w[i] = ((padded[o].toInt() and 0xff) shl 24) or ((padded[o + 1].toInt() and 0xff) shl 16) or
                    ((padded[o + 2].toInt() and 0xff) shl 8) or (padded[o + 3].toInt() and 0xff)
            }
            compress(state, w)
        }
    }

    fun sha256(message: ByteArray): ByteArray {
        val state = IV.copyOf()
        finish(state, 0, message, IntArray(64))
        return toBytes(state)
    }

    private fun toBytes(words: IntArray): ByteArray = ByteArray(words.size * 4) { i -> (words[i / 4] ushr (24 - 8 * (i % 4))).toByte() }

    /** L'état de SHA-256 après le bloc de la clé masquée par [pad]. */
    private fun keyState(key: ByteArray, pad: Int, w: IntArray): IntArray {
        val block = ByteArray(64) { i -> ((if (i < key.size) key[i].toInt() else 0) xor pad).toByte() }
        for (i in 0 until 16) {
            w[i] = ((block[i * 4].toInt() and 0xff) shl 24) or ((block[i * 4 + 1].toInt() and 0xff) shl 16) or
                ((block[i * 4 + 2].toInt() and 0xff) shl 8) or (block[i * 4 + 3].toInt() and 0xff)
        }
        return IV.copyOf().also { compress(it, w) }
    }

    /** Dérive [lengthBytes] octets (32 au plus par bloc, enchaînés au-delà). */
    fun derive(password: ByteArray, salt: ByteArray, iterations: Int, lengthBytes: Int = 32): ByteArray {
        require(iterations > 0 && lengthBytes > 0)
        val w = IntArray(64)
        val key = if (password.size > 64) sha256(password) else password
        val inner = keyState(key, 0x36, w)
        val outer = keyState(key, 0x5c, w)
        val out = ByteArray(lengthBytes)
        var produced = 0
        var blockIndex = 1
        while (produced < lengthBytes) {
            // U1 = HMAC(mot de passe, sel || numéro du bloc)
            val u = inner.copyOf()
            finish(u, 64, salt + byteArrayOf((blockIndex ushr 24).toByte(), (blockIndex ushr 16).toByte(), (blockIndex ushr 8).toByte(), blockIndex.toByte()), w)
            hmacOuter(outer, u, w)
            val t = u.copyOf()
            val state = IntArray(8)
            for (i in 1 until iterations) {
                // Ui = HMAC(mot de passe, U(i-1)) : le message fait 32 octets, un seul bloc de chaque côté.
                inner.copyInto(state)
                u.copyInto(w, 0, 0, 8)
                w[8] = 0x80000000.toInt()
                for (j in 9 until 15) w[j] = 0
                w[15] = 768
                compress(state, w)
                state.copyInto(u)
                hmacOuter(outer, u, w)
                for (j in 0 until 8) t[j] = t[j] xor u[j]
            }
            val bytes = toBytes(t)
            val n = minOf(32, lengthBytes - produced)
            bytes.copyInto(out, produced, 0, n)
            produced += n
            blockIndex++
        }
        return out
    }

    /** Remplace [digest] (le condensé intérieur) par le HMAC complet. */
    private fun hmacOuter(outer: IntArray, digest: IntArray, w: IntArray) {
        digest.copyInto(w, 0, 0, 8)
        w[8] = 0x80000000.toInt()
        for (j in 9 until 15) w[j] = 0
        w[15] = 768
        val state = outer.copyOf()
        compress(state, w)
        state.copyInto(digest)
    }
}
